import { useEffect, useRef, useState } from 'react';

/**
 * Decorative looping background video.
 *
 * The poster renders on the server and is all anyone gets until two things are
 * true: motion is allowed, and the element is near the viewport. The source is
 * ~10MB and sits well below the fold, so both gates matter.
 *
 * The reduced-motion gate alone was not enough. Motion is allowed for most
 * people, so the video mounted the moment hydration finished and pulled the
 * whole 10MB during initial load -- measured at 20MB of media before a single
 * scroll, on a decorative clip nobody had scrolled to yet. The observer holds
 * it back until it is actually worth fetching.
 *
 * Both branches are aria-hidden: the wrapper carries role="img" and the
 * description, so assistive tech gets one consistent label either way.
 */
export function BackgroundVideo({
  src,
  poster,
  className = '',
}: {
  src: string;
  poster: string;
  className?: string;
}) {
  const [motionAllowed, setMotionAllowed] = useState(false);
  const [nearViewport, setNearViewport] = useState(false);
  const holderRef = useRef<HTMLImageElement>(null);

  /* 400px of rootMargin so the fetch starts just before it scrolls in and the
     swap is not visible. Disconnects on the first hit -- once mounted the
     video stays mounted, so re-observing would only cost work. */
  useEffect(() => {
    const el = holderRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNearViewport(true);
          io.disconnect();
        }
      },
      { rootMargin: '400px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setMotionAllowed(!query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  if (!motionAllowed || !nearViewport) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        ref={holderRef}
        src={poster}
        alt=""
        aria-hidden="true"
        className={className}
      />
    );
  }

  return (
    <video
      autoPlay
      muted
      loop
      playsInline
      poster={poster}
      aria-hidden="true"
      className={className}
    >
      <source src={src} type="video/mp4" />
    </video>
  );
}
